import { OnInit, OnChanges, Input, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import * as Config from '../../shared/tokens/constants.tokens';
import { UnitLeader, MessageDialog, ConfirmDialog } from '../../shared';
import { MatDialog } from '@angular/material/dialog';

export abstract class LeaderFormComponent implements OnInit, OnChanges {
  leaderForm: FormGroup;
  selectedFile: File;
  previewUrlSelectedFile: string;
  deletePhoto = false;

  workingMessage = null;
  workedMessage = null;

  // we have to programmatically clear the value of our file picker, so we need this; not ideal, but oh well
  @ViewChild('fileInput', {static: true}) fileInput: ElementRef;
  @Input() abstract leader: UnitLeader;

  constructor(protected formBuilder: FormBuilder, protected dialog: MatDialog) {
    this.leaderForm = this.formBuilder.group({
      name: '',
      address: this.formBuilder.group({
        line1: '',
        line2: '',
        city: '',
        state: ['', [Validators.pattern(/^[A-Z]{2}$/)]],
        postalCode: ''
      }),
      addressPublic: false,
      workPhone: ['', [Validators.pattern(Config.Constants.phoneRegex)]],
      workPhonePublic: false,
      homePhone: ['', [Validators.pattern(Config.Constants.phoneRegex)]],
      homePhonePublic: false,
      cellPhone: ['', [Validators.pattern(Config.Constants.phoneRegex)]],
      cellPhonePublic: false,
      email: ['', [Validators.pattern(Config.Constants.emailRegex)]],
      emailPublic: false
    });
  }

  ngOnInit() {}

  ngOnChanges() {
    this.populateForm();
  }

  /**
   * Indicates whether or not there are unsaved changes on the leader.
   */
  hasUnsavedChanges(): boolean {
    return this.leaderForm.dirty;
  }

  /**
   * Tells you if the given form control has the given validation error.
   */
  hasError(controlName: string, error: string): boolean {
    // for some reason a prod build wouldn't let me do a leaderForm.get() in the html template (even though my other components can)
    // WEIRD!  so do it this way
    const formControl = this.leaderForm.get(controlName);
    if (formControl != null) {
      return (formControl.touched || formControl.dirty) && formControl.hasError(error);
    }

    return false;
  }

  /**
   * Cancels any changes the user's made to the ward's information.  Really, just repopulates the form from the details.
   */
  cancelChanges() {
    this.deletePhoto = false;
    this.selectedFile = null;
    this.fileInput.nativeElement.value = null;
    this.previewUrlSelectedFile = null;
    this.populateForm();
  }

  /**
   * Handles the user selecting a new photo for the leader.
   */
  handlePhoto(files: FileList) {
    if (files.length > 0) {
      const file = files.item(0);
      if (file.type === 'image/jpeg') {
        this.selectedFile = file;
        const fileReader = new FileReader();
        fileReader.onload = event => {
          this.previewUrlSelectedFile = <string>fileReader.result;
          this.deletePhoto = false;
        };
        fileReader.readAsDataURL(file);
        this.leaderForm.markAsDirty();
      }
    }
  }

  /**
   * If the user has selected an image to replace the leader's photo, clears the one they selected.  If not, marks the
   * leader's photo to be deleted (if there was previously one).
   *
   * @param fileInput The <input> for picking a file that our UI uses; need to clear its value if we're previewing.
   */
  removePhoto() {
    // if the user previously selected a new one, just remove that one
    if (this.selectedFile) {
      this.selectedFile = null;
      this.previewUrlSelectedFile = null;
      this.fileInput.nativeElement.value = null;
    } else if (this.leader.photoBase64) {
      this.deletePhoto = true;
      this.leaderForm.markAsDirty();
    }
  }

  /**
   * Copies information from the form and then calls the inheriting component to do the actual save.
   */
  save() {
    if (this.workingMessage || !this.leaderForm.dirty || !this.leaderForm.valid) {
      return;
    }

    // get our data from the form
    const leaderData = Object.assign({}, this.leader, this.leaderForm.value);

    // save it off (inheriting class responsible for actually doing it)
    this.saveLeader(leaderData, this.selectedFile, this.deletePhoto);
  }

  /**
   * Confirms the user wants to delete the leader and then tells the inheriting class to delete it.
   */
  delete() {
    if (this.workingMessage || this.isNewLeader()) {
      return;
    }

    const message = `Are you sure you want to remove the ${this.leader.position}'s information?`;
    const confirmDialog = this.dialog.open(ConfirmDialog, { autoFocus: false, data: { title: 'Please Confirm', message: message } });

    confirmDialog.afterClosed().subscribe(response => {
      if (response) {
        this.deleteLeader();
      }
    });
  }

  /**
   * Must be implemented by inheriting class so as to know if the leader we're working with is new (never been saved).
   */
  abstract isNewLeader();

  /**
   * Must be implemented by inheriting class so as to save the unit leader (either ward or stake leader).
   *
   * @param leader The leader information that needs saved
   * @param newPhoto The new photo, if any, for the leader
   * @param deletePhoto If not sending a new photo, we might want to delete the one already there; if so this will be true
   */
  protected abstract saveLeader(leader: UnitLeader, newPhoto: File, deletePhoto: boolean);

  /**
   * Must be implemented by inheriting class so as to delete the unit leader (either ward or stake leader).
   */
  protected abstract deleteLeader();

  /**
   * When we save the leader, let's inheriting classes let us know and we can reset the form, etc.
   */
  protected onSaved(updatedLeader: UnitLeader) {
    this.leader = updatedLeader;
    this.workedMessage = 'Leader information was updated';
    this.cancelChanges(); // reset things
  }

  /**
   * When we delete the leader, let's the inheriting classes let us know and we can reset the form, etc.
   */
  protected onDeleted(emptyLeader: UnitLeader) {
    this.leader = emptyLeader;
    this.workedMessage = 'Leader was removed';
    this.cancelChanges(); // reset things
  }

  /**
   * Shows an error dialog to the user.
   */
  protected showError(message: string) {
    this.dialog.open(MessageDialog, {
      data: { message: message, isError: true }
    });
  }

  /**
   * Populates our form from the ward's details.
   */
  private populateForm() {
    // reset the form
    this.leaderForm.reset();

    // patch in everything else; we already "patched" in the meetings
    this.leaderForm.patchValue(this.leader);
  }
}
